// ═══════════════════════════════════════════════════════════════════════
// TABELAS TRIBUTÁRIAS E LEGAIS CENTRALIZADAS
// Valores de referência (vigência 2026). Atualize aqui quando houver
// mudança nas regras oficiais — todas as calculadoras leem desta fonte.
// Cada tabela traz: ano, dataAtualizacao, fonte e observacao.
// Sempre confirme as tabelas oficiais atualizadas.
// ═══════════════════════════════════════════════════════════════════════

export const REFERENCIA = {
  anoINSS: 2026,
  anoIRRF: 2026,
  anoIR: 2026,
  atualizadoEm: "2026-01",
  aviso:
    "Simulação baseada nas tabelas configuradas para 2026. Regras oficiais podem mudar; confirme sempre com fontes oficiais ou profissional qualificado.",
};

// ── INSS — Empregado CLT (faixas progressivas mensais, vigência 2026) ──
export const INSS_EMPREGADO = {
  ano: 2026,
  dataAtualizacao: "2026-01",
  fonte: "INSS/Gov.br",
  observacao: "Tabela válida a partir da competência janeiro/2026, conforme INSS/Gov.br.",
  teto: 8475.55,
  salarioMinimo: 1621.0,
  faixas: [
    { limite: 1621.0, aliquota: 7.5 },
    { limite: 2902.84, aliquota: 9 },
    { limite: 4354.27, aliquota: 12 },
    { limite: 8475.55, aliquota: 14 },
  ],
};

// Outros tipos de contribuinte (alíquota sobre o salário de contribuição, respeitando o teto)
export const INSS_OUTROS = {
  contribuinte_individual: {
    aliquota: 20,
    teto: 8475.55,
    contribuicaoMinima: 324.2,
    contribuicaoMaxima: 1695.11,
    nota: "20% sobre o salário de contribuição, entre o salário mínimo e o teto (R$ 324,20 a R$ 1.695,11).",
  },
  facultativo: {
    aliquotaNormal: 20,
    teto: 8475.55,
    baixaRenda: { aliquota: 5, base: 1621.0, valor: 81.05 },
    facultativo11: { aliquota: 11, base: 1621.0, valor: 178.31 },
    nota: "Plano normal: 20% sobre o salário de contribuição. Baixa renda: 5% sobre o salário mínimo (R$ 81,05). Facultativo 11%: 11% sobre o salário mínimo (R$ 178,31).",
  },
  domestico: { aliquota_equipara: "empregado", teto: 8475.55, nota: "Segue a tabela progressiva do empregado CLT." },
};

// Cálculo progressivo do INSS (empregado CLT)
export function calcularINSS(salario) {
  const s = Math.min(salario, INSS_EMPREGADO.teto);
  let contrib = 0;
  let faixaAnterior = 0;
  const detalhe = [];
  for (const f of INSS_EMPREGADO.faixas) {
    if (salario > faixaAnterior) {
      const base = Math.min(salario, f.limite) - faixaAnterior;
      const valor = base * (f.aliquota / 100);
      contrib += valor;
      if (base > 0) detalhe.push({ faixa: faixaAnterior, ate: f.limite, base, aliquota: f.aliquota, valor });
      faixaAnterior = f.limite;
    }
  }
  const aliquotaEfetiva = salario > 0 ? (contrib / salario) * 100 : 0;
  return { contribuicao: contrib, aliquotaEfetiva, tetoAplicado: salario > INSS_EMPREGADO.teto, detalhe };
}

// ── IRRF — Tabela mensal (vigência 2026) ──
export const IRRF_MENSAL = {
  ano: 2026,
  dataAtualizacao: "2026-01",
  fonte: "Receita Federal/Gov.br",
  observacao: "Tabela de incidência mensal 2026, conforme Receita Federal/Gov.br.",
  faixas: [
    { limite: 2428.8, aliquota: 0, parcelaDeduzir: 0 },
    { limite: 2826.65, aliquota: 7.5, parcelaDeduzir: 182.16 },
    { limite: 3751.05, aliquota: 15, parcelaDeduzir: 394.16 },
    { limite: 4664.68, aliquota: 22.5, parcelaDeduzir: 675.49 },
    { limite: Infinity, aliquota: 27.5, parcelaDeduzir: 908.73 },
  ],
  deducaoPorDependente: 189.59,
  descontoSimplificado: 607.2,
  rendimentoIsentoMaior65: 1903.98,
  reducao: {
    faixas: [
      { limite: 5000, reducaoMaxima: 312.89, obs: "Redução de até R$ 312,89, de modo que o imposto devido seja zero." },
      { limite: 7350, obs: "Redução = R$ 978,62 − (0,133145 × rendimentos tributáveis sujeitos à incidência mensal)." },
      { limite: Infinity, reducao: 0, obs: "Sem redução." },
    ],
  },
};

// ── IR — Tabela anual (ano-calendário 2026 / exercício 2027) ──
export const IR_ANUAL = {
  ano: 2026,
  dataAtualizacao: "2026-01",
  fonte: "Receita Federal/Gov.br",
  observacao: "Tabela de incidência anual 2026 (exercício 2027), conforme Receita Federal/Gov.br.",
  faixas: [
    { limite: 29145.6, aliquota: 0, parcelaDeduzir: 0 },
    { limite: 33919.8, aliquota: 7.5, parcelaDeduzir: 2185.92 },
    { limite: 45012.6, aliquota: 15, parcelaDeduzir: 4729.91 },
    { limite: 55976.16, aliquota: 22.5, parcelaDeduzir: 8105.85 },
    { limite: Infinity, aliquota: 27.5, parcelaDeduzir: 10904.66 },
  ],
  deducaoPorDependente: 2275.08,
  descontoSimplificado: 17640.0,
  limiteDeducaoEducacao: 3561.5,
  limiteDeducaoSaude: Infinity,
  reducao: {
    faixas: [
      { limite: 60000, reducaoMaxima: 2694.15, obs: "Redução de até R$ 2.694,15, de modo que o imposto devido seja zero." },
      { limite: 88200, obs: "Redução = R$ 8.429,73 − (0,095575 × rendimentos tributáveis sujeitos ao ajuste anual)." },
      { limite: Infinity, reducao: 0, obs: "Sem redução." },
    ],
  },
};

// Cálculo do IRRF mensal
export function calcularIRRF(salarioBruto, { inss = 0, dependentes = 0, pensao = 0, outrasDeducoes = 0, simplificado = false } = {}) {
  const deducaoDep = dependentes * IRRF_MENSAL.deducaoPorDependente;
  let baseCalculo;
  let descontoAplicado = null;
  if (simplificado) {
    descontoAplicado = IRRF_MENSAL.descontoSimplificado;
    baseCalculo = Math.max(0, salarioBruto - descontoAplicado);
  } else {
    baseCalculo = Math.max(0, salarioBruto - inss - deducaoDep - pensao - outrasDeducoes);
  }
  let aliquota = 0;
  let parcelaDeduzir = 0;
  for (const f of IRRF_MENSAL.faixas) {
    if (baseCalculo <= f.limite) {
      aliquota = f.aliquota;
      parcelaDeduzir = f.parcelaDeduzir;
      break;
    }
    aliquota = f.aliquota;
    parcelaDeduzir = f.parcelaDeduzir;
  }
  const irrf = Math.max(0, baseCalculo * (aliquota / 100) - parcelaDeduzir);
  return {
    baseCalculo,
    aliquota,
    parcelaDeduzir,
    irrf,
    deducaoDependentes: deducaoDep,
    descontoAplicado,
    liquido: salarioBruto - inss - irrf,
  };
}

// Cálculo do IR anual
export function calcularIRAnual(rendimentos, { inss = 0, dependentes = 0, pensao = 0, medicas = 0, educacao = 0, prevPrivada = 0, outras = 0, simplificado = false } = {}) {
  const deducaoDep = dependentes * IR_ANUAL.deducaoPorDependente;
  let baseCalculo;
  let descontoAplicado = null;
  const deducoesLegais = inss + deducaoDep + pensao + medicas + Math.min(educacao, IR_ANUAL.limiteDeducaoEducacao * dependentes || IR_ANUAL.limiteDeducaoEducacao) + prevPrivada + outras;
  if (simplificado) {
    descontoAplicado = IR_ANUAL.descontoSimplificado;
    baseCalculo = Math.max(0, rendimentos - descontoAplicado);
  } else {
    baseCalculo = Math.max(0, rendimentos - deducoesLegais);
  }
  let aliquota = 0;
  let parcelaDeduzir = 0;
  for (const f of IR_ANUAL.faixas) {
    if (baseCalculo <= f.limite) {
      aliquota = f.aliquota;
      parcelaDeduzir = f.parcelaDeduzir;
      break;
    }
    aliquota = f.aliquota;
    parcelaDeduzir = f.parcelaDeduzir;
  }
  const impostoDevido = Math.max(0, baseCalculo * (aliquota / 100) - parcelaDeduzir);
  return {
    baseCalculo,
    aliquota,
    parcelaDeduzir,
    impostoDevido,
    deducoesLegais,
    deducaoDependentes: deducaoDep,
    descontoAplicado,
  };
}

// ── Seguro-Desemprego (vigência 2026) ──
export const SEGURO_DESEMPREGO = {
  ano: 2026,
  dataAtualizacao: "2026-01-11",
  fonte: "Ministério do Trabalho e Emprego/Gov.br",
  observacao: "Tabela 2026 do Seguro-Desemprego, conforme Ministério do Trabalho e Emprego/Gov.br.",
  salarioMinimo: 1621.0,
  teto: 2518.65,
  faixas: [
    { ate: 2222.17, multiplicador: 0.8, obs: "80% da média salarial" },
    { ate: 3703.99, multiplicador: 0.5, adicional: 1777.74, obs: "80% até R$ 2.222,17 + 50% do excedente" },
    { ate: Infinity, teto: 2518.65, obs: "Teto de R$ 2.518,65" },
  ],
  // Número de parcelas conforme meses trabalhados e número de solicitações
  parcelas: [
    { mesesTrabalhados: [6, 11], primeiraSolicitacao: 4, demaisSolicitacoes: 3 },
    { mesesTrabalhados: [12, 23], primeiraSolicitacao: 5, demaisSolicitacoes: 4 },
    { mesesTrabalhados: [24, Infinity], primeiraSolicitacao: 6, demaisSolicitacoes: 5 },
  ],
};

export function calcularSeguroDesemprego(mediaSalarial, mesesTrabalhados, numSolicitacoes) {
  const media = mediaSalarial;
  const faixa2 = SEGURO_DESEMPREGO.faixas[1];
  let valor;
  if (media <= SEGURO_DESEMPREGO.faixas[0].ate) {
    valor = media * 0.8;
  } else if (media <= faixa2.ate) {
    valor = SEGURO_DESEMPREGO.faixas[0].ate * 0.8 + (media - SEGURO_DESEMPREGO.faixas[0].ate) * 0.5;
  } else {
    valor = SEGURO_DESEMPREGO.teto;
  }
  valor = Math.min(valor, SEGURO_DESEMPREGO.teto);
  valor = Math.max(valor, SEGURO_DESEMPREGO.salarioMinimo);
  const primeira = numSolicitacoes <= 1;
  let numParcelas = 0;
  for (const p of SEGURO_DESEMPREGO.parcelas) {
    if (mesesTrabalhados >= p.mesesTrabalhados[0] && mesesTrabalhados <= p.mesesTrabalhados[1]) {
      numParcelas = primeira ? p.primeiraSolicitacao : p.demaisSolicitacoes;
      break;
    }
  }
  return {
    valorParcela: valor,
    numParcelas,
    totalEstimado: valor * numParcelas,
    primeiraSolicitacao: primeira,
  };
}

// ── Encargos sobre folha (Custo de Funcionário CLT) ──
export const ENCARGOS_CLT = {
  fgts: 8,
  inssPatronal: 20,
  rat: 2, // Risco Ambiental do Trabalho (1% a 3%)
  sistemaS: 5.8, // Terceiros (S/Senai, Sesc, Incra, etc.)
  provisao13: 8.333, // 1/12 de 100%
  provisaoFerias: 11.11, // 1/12 de (100% + 1/3)
};

export const REGIMES_EMPRESA = {
  simples: { nome: "Simples Nacional", inssPatronalReduzido: true, nota: "No Simples, o INSS patronal pode ser substituído pelo DAS; verifique o anexo aplicável." },
  presumido: { nome: "Lucro Presumido", inssPatronalReduzido: false },
  real: { nome: "Lucro Real", inssPatronalReduzido: false },
  personalizado: { nome: "Personalizado", inssPatronalReduzido: false },
};

export function calcularCustoFuncionario(salario, beneficios, encargosEditaveis, regime) {
  const e = { ...ENCARGOS_CLT, ...encargosEditaveis };
  const pctEncargos =
    e.fgts + (REGIMES_EMPRESA[regime]?.inssPatronalReduzido ? 0 : e.inssPatronal) + e.rat + e.sistemaS + e.provisao13 + e.provisaoFerias;
  const encargosValor = salario * (pctEncargos / 100);
  const benefValor = Object.values(beneficios).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const custoMensal = salario + encargosValor + benefValor;
  return {
    salario,
    encargosValor,
    pctEncargos,
    benefValor,
    custoMensal,
    custoAnual: custoMensal * 12,
    detalhe: {
      fgts: salario * (e.fgts / 100),
      inssPatronal: REGIMES_EMPRESA[regime]?.inssPatronalReduzido ? 0 : salario * (e.inssPatronal / 100),
      rat: salario * (e.rat / 100),
      sistemaS: salario * (e.sistemaS / 100),
      provisao13: salario * (e.provisao13 / 100),
      provisaoFerias: salario * (e.provisaoFerias / 100),
    },
  };
}

// ── IR regressivo (renda fixa) — vigência 2026 ──
export const IR_REGRESSIVO = {
  ano: 2026,
  dataAtualizacao: "2026-01",
  fonte: "Receita Federal",
  observacao: "Tabela regressiva de IR para aplicações de renda fixa, conforme Receita Federal.",
  faixas: [
    { ateDias: 180, aliquota: 22.5 },
    { ateDias: 360, aliquota: 20 },
    { ateDias: 720, aliquota: 17.5 },
    { ateDias: Infinity, aliquota: 15 },
  ],
};

export function aliquotaIRRegressivo(dias) {
  for (const f of IR_REGRESSIVO.faixas) {
    if (dias <= f.ateDias) return f.aliquota;
  }
  return 15;
}

// ── IOF sobre renda fixa (tabela decrescente — base 96% no 1º dia) ──
// IOF resgatável: 96% no 1º dia, decaindo até 0% no 30º dia.
export function aliquotaIOF(dias) {
  if (dias >= 30) return 0;
  // Fórmula simplificada oficial: IOF = 96% - (dias-1) * taxaDiaria
  const taxaDiaria = 96 / 30;
  return Math.max(0, 96 - (dias - 1) * (96 / 30));
}

// ── CDI de referência (editável) ──
export const CDI_REFERENCIA = {
  cdiAnual: 10.0, // % ao ano — ajuste conforme vigência
  selicAnual: 10.0,
  nota: "CDI usado na simulação. Atualize com o valor vigente antes de simular.",
};

// ── Margem consignável por tipo de público (%) ──
export const MARGEM_CONSIGNAVEL = {
  clt: 30,
  aposentado: 30,
  servidor: 35,
  outro: 30,
  nota: "Limites de margem podem variar conforme regra vigente, convênio, banco e perfil do tomador.",
};